import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Modal from '../../../../shared/_TBComponents/Modal';
import Icon from '../../../../shared/_TBComponents/Icon';
import {
  fetchSharedWorkoutInfo, shareSavedWorkout, unshareSavedWorkout,
} from '../../../calendar/ducks/calendarActions';
import { showErrorToast } from '../../../../shared/utils/toastManager';

// Random suffix avoids slug collisions with existing shares.
const buildSlug = (name) => {
  const base = (name || 'program')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'program';
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base}-${suffix}`;
};

// The shared Modal's content wrapper is a flex row; without width:100% this child
// shrinks to its intrinsic width and leaves dead space on the right.
const Body = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
  font-family: "Nunito Sans";
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #D6D6D6;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 24px;
  font-weight: 900;
  letter-spacing: -0.24px;
  color: #1A1A1A;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const Divider = styled.div`
  height: 1px;
  background: #D6D6D6;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px 64px;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProgramLine = styled.p`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
  overflow-wrap: break-word;
`;

const Description = styled.p`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
`;

const HintText = styled.p`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #616161;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LinkText = styled.a`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #1787FB;
  text-decoration: underline;

  &:hover { text-decoration: underline; }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 6px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.48px;
  color: #1A1A1A;

  &:hover { opacity: 0.85; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 24px;
`;

const FooterButton = styled.button`
  height: 36px;
  padding: 6px 16px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.48px;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  border: ${(p) => (p.danger ? '1px solid #FF0505' : 'none')};
  background: ${(p) => {
    if (p.danger) return '#FFFFFF';
    if (!p.primary) return 'none';
    return p.disabled ? '#E0E0E0' : '#1A1A1A';
  }};
  color: ${(p) => {
    if (p.primary) return '#FFFFFF';
    return p.danger ? '#FF0505' : '#1A1A1A';
  }};

  &:hover { opacity: ${(p) => (p.disabled ? 1 : 0.85)}; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const ShareProgramModal = ({
  isOpen, onClose, program, accountCode,
}) => {
  const dispatch = useDispatch();
  const [shareUrl, setShareUrl] = useState(null);
  const [alreadyShared, setAlreadyShared] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  // Reset transient state each time the modal opens for a (different) program,
  // then rehydrate the share status so an existing link shows correctly.
  useEffect(() => {
    if (isOpen && program?.id) {
      setShareUrl(null);
      setAlreadyShared(false);
      setIsSharing(false);
      setIsDisabling(false);
      setCopied(false);
      setIsLoadingStatus(true);
      dispatch(fetchSharedWorkoutInfo(accountCode, program.id, {
        onSuccess: (info) => {
          if (info?.isShared && info?.sharedWorkout?.slug) {
            setShareUrl(`${window.location.origin}/shared-workouts/${info.sharedWorkout.slug}`);
          }
          setIsLoadingStatus(false);
        },
        // Status is a convenience — on failure fall back to the create flow
        // (its "already being shared" error still flips alreadyShared).
        onError: () => setIsLoadingStatus(false),
      }));
    }
  }, [isOpen, program]);

  const handleShare = () => {
    const slug = buildSlug(program?.programName);
    setIsSharing(true);
    dispatch(shareSavedWorkout(accountCode, program.id, slug, {
      onSuccess: () => {
        setShareUrl(`${window.location.origin}/shared-workouts/${slug}`);
        setIsSharing(false);
      },
      onError: (err) => {
        const msg = err?.response?.headers?.message || err?.response?.data?.message || '';
        if (/already being shared/i.test(msg)) setAlreadyShared(true);
        setIsSharing(false);
      },
    }));
  };

  const handleDisable = () => {
    setIsDisabling(true);
    dispatch(unshareSavedWorkout(accountCode, program.id, {
      onSuccess: () => { setIsDisabling(false); setShareUrl(null); setAlreadyShared(false); },
      onError: () => setIsDisabling(false),
    }));
  };

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      showErrorToast('Couldn’t copy the link — copy it manually');
    });
  };

  const isShared = !!shareUrl || alreadyShared;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      hideTitle
      customMaxWidth='600px'
      customMinHeight='0'
      customPadding='0'
      customBorderRadius='8px'
      hideButton
    >
      {program && (
        <Body>
          <HeaderRow>
            <Title>Share Program</Title>
            <CloseButton type='button' onClick={onClose} aria-label='Close'>
              <Icon icon='remove' size={16} color='#929292' />
            </CloseButton>
          </HeaderRow>
          <Divider />
          <Content>
            <TextBlock>
              <ProgramLine>{`Program: ${program.programName}`}</ProgramLine>
              <Description>
                {isShared
                  ? 'Sharing is enabled for this program. Anyone with the link can view and import it.'
                  : 'Create a public link so anyone can view and import it. The link must not contain any spaces or special characters.'}
              </Description>
            </TextBlock>

            {shareUrl && (
              <LinkRow>
                <LinkText href={shareUrl} target='_blank' rel='noopener noreferrer'>{shareUrl}</LinkText>
                <CopyButton type='button' onClick={handleCopy}>
                  <Icon icon='duplicate' size={24} color='#1A1A1A' />
                  {copied ? 'Copied!' : 'Copy Link'}
                </CopyButton>
              </LinkRow>
            )}

            {alreadyShared && !shareUrl && (
              <HintText>
                This program already has an active link. Disable it to generate a fresh one.
              </HintText>
            )}
          </Content>
          <Divider />
          <FooterBar>
            <FooterButton type='button' onClick={onClose}>
              {isShared ? 'Close' : 'Cancel'}
            </FooterButton>
            {isShared ? (
              <FooterButton
                type='button'
                danger
                disabled={isDisabling}
                onClick={handleDisable}
              >
                Disable Sharing
              </FooterButton>
            ) : (
              <FooterButton
                type='button'
                primary
                disabled={isSharing || isLoadingStatus}
                onClick={handleShare}
              >
                Create Link
              </FooterButton>
            )}
          </FooterBar>
        </Body>
      )}
    </Modal>
  );
};

ShareProgramModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountCode: PropTypes.number,
  program: PropTypes.shape({
    id: PropTypes.number,
    programName: PropTypes.string,
  }),
};

ShareProgramModal.defaultProps = {
  accountCode: undefined,
  program: null,
};

export default ShareProgramModal;
